import { getPendingSeatOrder, type SeatOrder } from "@/lib/licenseQuota/api";

export type InvoiceType = "b2c" | "b2b";

export interface InvoiceProfile {
  type: InvoiceType;
  title: string;
  taxId: string;
  email: string;
  address: string;
}

export type InvoiceStatus = "pending" | "issued" | "void";

export interface InvoiceRecord {
  id: string;
  number: string | null;
  issuedAt: string | null;
  createdAt: string;
  sourceOrderId: string;
  subtotal: number;
  tax: number;
  total: number;
  status: InvoiceStatus;
}

export const INVOICE_PROFILE_STORAGE_KEY = "sfc.invoice.profile";
export const INVOICE_RECORDS_STORAGE_KEY = "sfc.invoice.records";

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

const TAX_ID_WEIGHTS = [1, 2, 1, 2, 1, 2, 4, 1];

export function isValidTaxId(taxId: string): boolean {
  if (!/^\d{8}$/.test(taxId)) return false;
  const sum = taxId.split("").reduce((s, ch, i) => {
    const p = Number(ch) * TAX_ID_WEIGHTS[i];
    return s + Math.floor(p / 10) + (p % 10);
  }, 0);
  if (sum % 5 === 0) return true;
  return taxId[6] === "7" && (sum + 1) % 5 === 0;
}

export async function getInvoiceProfile(): Promise<InvoiceProfile | null> {
  const parsed = readJson<InvoiceProfile | null>(INVOICE_PROFILE_STORAGE_KEY, null);
  return parsed?.type && parsed?.email ? parsed : null;
}

export async function saveInvoiceProfile(profile: InvoiceProfile): Promise<void> {
  writeJson(INVOICE_PROFILE_STORAGE_KEY, profile);
}

function fromSeatOrder(order: SeatOrder): InvoiceRecord {
  return {
    id: `INV-${order.id}`,
    number: null,
    issuedAt: null,
    createdAt: order.createdAt,
    sourceOrderId: order.id,
    subtotal: order.subtotal,
    tax: order.tax,
    total: order.total,
    status: "pending",
  };
}

export async function listInvoices(): Promise<InvoiceRecord[]> {
  const stored = readJson<InvoiceRecord[]>(INVOICE_RECORDS_STORAGE_KEY, []);
  const records = Array.isArray(stored) ? stored : [];
  const pending = await getPendingSeatOrder();
  const merged =
    pending && !records.some((r) => r.sourceOrderId === pending.id)
      ? [fromSeatOrder(pending), ...records]
      : records;
  return [...merged].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}
